import { QRCodeSVG } from "qrcode.react";
import { photoHref } from "@/lib/api";

const Field = ({ k, v, size = "12pt", mono = false }) => (
  <div style={{ minWidth: 0 }}>
    <div className="lbl">{k}</div>
    <div className={`val${mono ? " font-mono-data" : ""}`} style={{ fontSize: size }}>
      {v || "—"}
    </div>
  </div>
);

// 4 x 6 in label for a fixture / cutout — reuses the shared .label-4x6 print styles.
export const FixtureLabel = ({ fixture: f, context = {}, forwardRef }) => {
  const url = f.token
    ? `${window.location.origin}/confirm/fixture/${f.token}`
    : context.url || window.location.origin;
  const tags = (f.tags || []).join(", ");
  const s = f._sink || null;
  const brand = f.sink_brand || s?.brand || "";
  const model = f.sink_model || s?.model || "";
  const sinkName =
    [brand, model].filter(Boolean).join(" ").trim() ||
    f.sink ||
    (f.in_stock === false ? "Customer-supplied" : "");
  const sinkQty =
    s && s.stocked !== false
      ? `${s.quantity ?? 0} on hand`
      : s
        ? "Customer-supplied"
        : "";
  const reservedText = f._reserved
    ? "Reserved"
    : f.sink_id
      ? "Not reserved"
      : "";
  const sinkPhoto =
    s?.photo ||
    (s?.photos || [])
      .map((p) => p?.thumb_path || p?.storage_path || p?.path || p?.thumb)
      .find(Boolean) ||
    "";

  return (
    <div
      className="label-4x6 print-label"
      ref={forwardRef}
      data-testid="fixture-label-sheet"
    >
      {context.logoUrl ? (
        <div
          className="print-fixed print-fixed-logo"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: "2px solid #000",
            paddingBottom: "0.06in",
            marginBottom: "0.08in",
          }}
        >
          <img
            src={context.logoUrl}
            alt=""
            style={{ height: "0.46in", objectFit: "contain" }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
            data-testid="fixture-label-logo"
          />
        </div>
      ) : (
        <div
          style={{
            fontSize: "13pt",
            fontWeight: 800,
            letterSpacing: "0.14em",
            textAlign: "center",
            borderBottom: "2px solid #000",
            paddingBottom: "0.06in",
            marginBottom: "0.08in",
          }}
        >
          BRIONI GRANITE
        </div>
      )}
      <div className="lbl-head">
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: "8pt", fontWeight: 700, letterSpacing: "0.18em" }}>
            FIXTURE / CUTOUT
          </div>
          <div style={{ fontSize: "20pt", fontWeight: 700, lineHeight: 1.05 }}>
            {f.area || "Fixture"}
          </div>
          {f.piece ? (
            <div style={{ fontSize: "11pt", fontWeight: 600 }}>{f.piece}</div>
          ) : null}
        </div>
        <div style={{ textAlign: "right", fontSize: "8pt", fontWeight: 700, flexShrink: 0 }}>
          <div style={{ border: "2px solid #000", padding: "2px 6px" }}>
            {f.confirmed ? "CONFIRMED" : "PENDING"}
          </div>
          {f._reserved ? (
            <div
              data-testid="fixture-label-reserved"
              style={{ marginTop: 3, border: "2px solid #000", padding: "1px 6px" }}
            >
              SINK RESERVED
            </div>
          ) : null}
          {context.code ? (
            <div className="font-mono-data" style={{ marginTop: 3 }}>
              {context.code}
            </div>
          ) : null}
        </div>
      </div>

      {f.warning ? (
        <div
          data-testid="fixture-label-warning"
          style={{
            margin: "0.08in 0",
            border: "2px solid #000",
            padding: "3px 6px",
            fontSize: "11pt",
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.03em",
          }}
        >
          {"\u26A0 "}
          {f.warning}
        </div>
      ) : null}

      <div className="lbl-body">
        <div className="lbl-grid">
          <Field k="Customer" v={context.customer} />
          <Field k="Project" v={context.title} />
          <Field k="Mount" v={f.mount || s?.sink_type} />
          <Field k="Faucet Holes" v={f.faucet_holes} />
        </div>
        <Field k="Accessory Holes" v={f.accessory_holes} />
        <div style={{ display: "flex", gap: "0.1in", alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {(brand || model) ? (
              <div className="lbl-grid">
                <Field k="Sink Brand" v={brand} />
                <Field k="Sink Model" v={model} mono />
              </div>
            ) : (
              <Field k="Sink" v={sinkName} />
            )}
          </div>
          {sinkPhoto ? (
            <img
              src={photoHref(sinkPhoto)}
              alt=""
              data-testid="fixture-label-sink-photo"
              style={{
                height: "1in",
                width: "1in",
                objectFit: "cover",
                border: "2px solid #000",
                flexShrink: 0,
              }}
            />
          ) : null}
        </div>
        {(s || reservedText) ? (
          <div className="lbl-grid">
            <Field k="Sink Stock" v={sinkQty} />
            <Field k="Location" v={s?.location} />
            <Field k="Reservation" v={reservedText} />
            {s?.size ? <Field k="Sink Size" v={s.size} /> : null}
          </div>
        ) : null}
        {tags ? <Field k="Tags" v={tags} /> : null}
        {f.notes ? (
          <div style={{ borderTop: "1px solid #000", paddingTop: "0.04in" }}>
            <div className="lbl">Notes</div>
            <div className="lbl-notes">{f.notes}</div>
          </div>
        ) : null}
      </div>

      <div className="lbl-foot">
        <div style={{ fontSize: "7pt", color: "#333", maxWidth: "2.3in" }}>
          {context.address || context.title || ""}
          <div className="font-mono-data" style={{ fontSize: "7.5pt", fontWeight: 700 }}>
            Scan to confirm ready
          </div>
        </div>
        <QRCodeSVG value={url} size={78} level="M" bgColor="#ffffff" fgColor="#000000" />
      </div>
    </div>
  );
};
